import { useParams, Link } from 'react-router-dom';
import SEOMeta from '@/components/feature/SEOMeta';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { categories, getMoviesByCategory, allMovies } from '@/mocks/movies';
import { useState, useMemo } from 'react';

const categoryMetaMap: Record<string, { title: string; description: string; keywords: string }> = {
  movie: {
    title: '电影大全 - 牛牛免费看高清电影 | 最新热门电影在线观看',
    description: '牛牛影视电影大全频道，汇集最新热门电影、经典电影、院线大片，免费高清在线观看。动作片、喜剧片、科幻片、爱情片应有尽有，每日更新不停歇。',
    keywords: '电影大全,免费电影,高清电影在线观看,最新电影,热门电影,动作片,喜剧片,科幻片,牛牛影视',
  },
  tv: {
    title: '电视剧全集 - 牛牛免费看电视剧 | 热门连续剧免费在线观看',
    description: '牛牛影视电视剧频道，汇集最新国产剧、韩剧、美剧、日剧全集免费在线观看。古装剧、都市剧、悬疑剧一网打尽，追剧不用等，高清流畅不缓冲。',
    keywords: '电视剧全集,免费电视剧,热门电视剧,国产剧,韩剧,追剧在线观看,连续剧,牛牛影视',
  },
  variety: {
    title: '综艺节目 - 牛牛免费看综艺 | 热门综艺免费在线观看',
    description: '牛牛影视综艺频道，汇集热门综艺节目免费在线观看。真人秀、音乐竞技、脱口秀、户外综艺，每周更新，欢乐不停歇。',
    keywords: '综艺节目,免费综艺,热门综艺,真人秀,音乐竞技,脱口秀在线观看,牛牛影视',
  },
  anime: {
    title: '动漫专区 - 牛牛免费看动漫 | 热门国漫日漫免费在线观看',
    description: '牛牛影视动漫频道，汇集热门国漫、日漫免费在线观看。玄幻修仙、热血战斗、恋爱日常，二次元世界的精彩全在这里。',
    keywords: '动漫,免费动漫,国漫,日漫,斗破苍穹,完美世界,凡人修仙传,牛牛影视',
  },
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'latest' | 'hot'>('hot');
  const [selectedTag, setSelectedTag] = useState<string>('all');

  const category = categories.find(c => c.slug === slug);
  const meta = slug ? categoryMetaMap[slug] : null;

  const filteredMovies = useMemo(() => {
    if (!slug) return [];
    let movies = getMoviesByCategory(slug);
    if (selectedTag !== 'all') {
      movies = movies.filter(m => m.tags.includes(selectedTag));
    }
    movies = [...movies].sort((a, b) => {
      if (sortBy === 'rating') return b.rating - a.rating;
      if (sortBy === 'latest') return b.year - a.year;
      return (a.hotRank || 99) - (b.hotRank || 99);
    });
    return movies;
  }, [slug, sortBy, selectedTag]);

  const allTags = useMemo(() => {
    if (!slug) return [];
    const movies = getMoviesByCategory(slug);
    const tagSet = new Set<string>();
    movies.forEach(m => m.tags.forEach(t => tagSet.add(t)));
    return Array.from(tagSet);
  }, [slug]);

  if (!category || !meta) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
          <p className="mt-4 text-foreground-500">分类不存在</p>
          <Link to="/" className="mt-3 inline-block text-primary-600 hover:text-primary-700 font-medium cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        canonicalUrl={`https://niuniu.tv/category/${slug}`}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.label },
                ]}
              />

              <div className="mt-6 mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className={`${category.icon} text-lg`}></i>
                  </span>
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                    {category.label}
                  </h1>
                </div>
                <p className="text-sm text-foreground-500 max-w-2xl">{meta.description}</p>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setSelectedTag('all')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                      selectedTag === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    全部
                  </button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSelectedTag(tag)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                        selectedTag === tag
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-1.5 bg-background-100 rounded-full p-1">
                  {[
                    { value: 'hot' as const, label: '最热' },
                    { value: 'rating' as const, label: '评分' },
                    { value: 'latest' as const, label: '最新' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSortBy(opt.value)}
                      className={`px-4 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                        sortBy === opt.value
                          ? 'bg-background-50 text-foreground-950'
                          : 'text-foreground-500 hover:text-foreground-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                  <p className="mt-4 text-foreground-500">暂无相关内容</p>
                </div>
              )}
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}